import React, { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CalendarIcon, RefreshCcw, ChevronDown } from "lucide-react";
import { format } from "date-fns";

const QUICK = [
  { k: "1h", l: "Last 1h", ms: 60 * 60 * 1000 },
  { k: "6h", l: "Last 6h", ms: 6 * 60 * 60 * 1000 },
  { k: "24h", l: "Last 24h", ms: 24 * 60 * 60 * 1000 },
  { k: "7d", l: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { k: "30d", l: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000 },
];

export default function DateRangeBar({ range, setRange, onRefresh }) {
  const [open, setOpen] = useState(false);

  const applyQuick = (ms) => {
    const end = new Date();
    const start = new Date(end.getTime() - ms);
    setRange({ start, end });
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="date-range-bar">
      <div className="label-tech">RANGE</div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid="quick-range-trigger"
            variant="outline"
            className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-9 font-mono-tech text-xs"
          >
            QUICK SELECT <ChevronDown className="w-3 h-3 ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-zinc-900/80 backdrop-blur-xl border-white/10">
          {QUICK.map((q) => (
            <DropdownMenuItem
              key={q.k}
              data-testid={`quick-${q.k}`}
              onClick={() => applyQuick(q.ms)}
              className="font-mono-tech text-xs"
            >
              {q.l}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="date-range-picker"
            variant="outline"
            className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-9 font-mono-tech text-xs"
          >
            <CalendarIcon className="w-3 h-3 mr-2" />
            {format(range.start, "dd MMM HH:mm")} → {format(range.end, "dd MMM HH:mm")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="bg-zinc-900/80 backdrop-blur-xl border-white/10 p-0" align="start">
          <Calendar
            mode="range"
            selected={{ from: range.start, to: range.end }}
            onSelect={(r) => {
              if (r?.from && r?.to) {
                const start = new Date(r.from); start.setHours(0, 0, 0, 0);
                const end = new Date(r.to); end.setHours(23, 59, 59, 999);
                setRange({ start, end });
                setOpen(false);
              }
            }}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>

      <Button
        data-testid="refresh-btn"
        onClick={onRefresh}
        variant="outline"
        className="border-white/15 bg-transparent hover:bg-white/10 rounded-sm h-9"
      >
        <RefreshCcw className="w-3 h-3 mr-2" /> REFRESH
      </Button>
    </div>
  );
}
